import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from './Toast';
import { useModal } from '../../hooks/useModal';
import { notificationService } from '../../services/api';
import './Navbar.css';

const Navbar = ({ user, onLogout, toggleSidebar, companyProfile }) => {
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Form states for edit profile
  const [editProfileData, setEditProfileData] = useState({
    name: user.name,
    email: user.email,
    username: user.username || 'admin'
  });

  // Form states for change password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Sync local state with user prop updates
  useEffect(() => {
    setEditProfileData({
      name: user.name,
      email: user.email,
      username: user.username || 'admin'
    });
  }, [user]);

  // Hide navbar when modals are open
  useModal(showProfileModal || showEditProfileModal || showChangePasswordModal || showNotificationsModal, () => {
    setShowProfileModal(false);
    setShowEditProfileModal(false);
    setShowChangePasswordModal(false);
    setShowNotificationsModal(false);
  });

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch notifications on mount
  useEffect(() => {
    loadNotifications();
    // Optional: Poll every minute for new notifications
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationService.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Map notification types to routes
  const getNotificationRoute = (type) => {
    const routeMap = {
      'tender': '/tenders',
      'stock': '/stock',
      'quotation': '/quotations',
      'project': '/projects',
      'organization': '/organizations',
      'contact': '/contacts',
      'record': '/records'
    };
    return routeMap[type] || '/';
  };

  const handleNotificationClick = async (notification) => {
    try {
      const route = getNotificationRoute(notification.type);

      // Mark as read in backend
      if (!notification.read) {
        await notificationService.markRead(notification.id);
        // Update local state
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, read: true } : n
        ));
      }

      setShowNotificationsModal(false);
      navigate(route);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setToastMessage('All notifications marked as read');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setToastMessage('');
        setToastType('success');
      }, 3000);
    } catch (error) {
      console.error('Error marking all as read:', error);
      setToastMessage('Failed to mark notifications as read');
      setToastType('error');
      setShowToast(true);
    }
  };

  return (
    <>
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => {
            setShowToast(false);
            setToastMessage('');
            setToastType('success');
          }}
        />
      )}
      <nav className="navbar">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            {companyProfile?.logo && (
              <img
                src={companyProfile.logo}
                alt="Logo"
                style={{
                  height: '50px',
                  width: 'auto',
                  marginRight: '15px'
                }}
              />
            )}
            <div className="navbar-welcome">
              <h4 className="mb-0 navbar-welcome-title" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: '700',
                fontSize: '1.3rem',
                letterSpacing: '0.3px'
              }}>
                Welcome back, {user.name}
              </h4>
              <small className="navbar-welcome-role" style={{
                fontWeight: '600',
                color: 'var(--gray-600)',
                fontSize: '0.875rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                {user.role}
              </small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-link d-lg-none p-0 me-2"
              onClick={toggleSidebar}
              style={{ color: 'var(--gray-700)' }}
            >
              <i className="bi bi-list fs-1"></i>
            </button>
            <div
              className="user-info position-relative"
              onClick={() => setShowNotificationsModal(true)}
              style={{ cursor: 'pointer' }}
              title="Notifications"
            >
              <i className="bi bi-bell fs-5" style={{ color: 'var(--gray-700)' }}></i>
              {unreadCount > 0 && (
                <span
                  className="badge bg-danger position-absolute top-0 start-100 translate-middle"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    animation: 'pulse 2s infinite'
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="dropdown position-relative">
              <button
                className="btn btn-link text-decoration-none dropdown-toggle user-profile-btn"
                type="button"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                aria-expanded={showUserDropdown}
                style={{
                  padding: '8px',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all 0.3s ease',
                  border: '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <i className="bi bi-person-circle fs-4" style={{ color: 'var(--primary-color)' }}></i>
              </button>
              {showUserDropdown && (
                <>
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100"
                    style={{ zIndex: 1040 }}
                    onClick={() => setShowUserDropdown(false)}
                  ></div>
                  <ul
                    className="dropdown-menu dropdown-menu-end show position-absolute"
                    style={{ zIndex: 1050, top: '100%', right: 0 }}
                  >
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowUserDropdown(false);
                          setShowProfileModal(true);
                        }}
                      >
                        <i className="bi bi-person me-2"></i>Profile
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowUserDropdown(false);
                          setShowNotificationsModal(true);
                        }}
                      >
                        <i className="bi bi-bell me-2"></i>Notifications
                        {unreadCount > 0 && (
                          <span className="badge bg-danger ms-2">{unreadCount}</span>
                        )}
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowUserDropdown(false);
                          onLogout();
                        }}
                      >
                        <i className="bi bi-box-arrow-right me-2"></i>Logout
                      </button>
                    </li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Profile Modal */}
      {showProfileModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowProfileModal(false);
            }
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0, maxWidth: '500px', width: '100%' }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">User Profile</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowProfileModal(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <i className="bi bi-person-circle" style={{ fontSize: '80px', color: '#667eea' }}></i>
                </div>
                <div className="row mb-3">
                  <div className="col-4"><strong>Name:</strong></div>
                  <div className="col-8">{user.name}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4"><strong>Username:</strong></div>
                  <div className="col-8">{user.username || 'admin'}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4"><strong>Email:</strong></div>
                  <div className="col-8">{user.email}</div>
                </div>
                <div className="row mb-3">
                  <div className="col-4"><strong>Role:</strong></div>
                  <div className="col-8">
                    <span className="badge bg-primary">{user.role}</span>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-4"><strong>Status:</strong></div>
                  <div className="col-8">
                    <span className="badge bg-success">Active</span>
                  </div>
                </div>
                <hr />
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowProfileModal(false);
                      setShowEditProfileModal(true);
                      setEditProfileData({
                        name: user.name,
                        email: user.email,
                        username: user.username || 'admin'
                      });
                    }}
                  >
                    <i className="bi bi-pencil me-2"></i>Edit Profile
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowProfileModal(false);
                      setShowChangePasswordModal(true);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                    }}
                  >
                    <i className="bi bi-key me-2"></i>Change Password
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowProfileModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowNotificationsModal(false);
            }
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0, maxWidth: '600px', width: '100%' }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-bell me-2"></i>Notifications
                  {unreadCount > 0 && (
                    <span className="badge bg-danger ms-2">{unreadCount} unread</span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNotificationsModal(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-bell-slash" style={{ fontSize: '48px', color: '#ccc' }}></i>
                    <p className="text-muted mt-3">No notifications</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`list-group-item list-group-item-action ${!notification.read ? 'bg-light' : ''
                          }`}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleNotificationClick(notification)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = notification.read ? '' : 'rgba(248, 249, 250, 1)';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div className="d-flex w-100 justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <h6 className="mb-0 me-2">{notification.title}</h6>
                              {!notification.read && (
                                <span className="badge bg-primary">New</span>
                              )}
                            </div>
                            <p className="mb-1 text-muted">{notification.message}</p>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>{notification.time}
                            </small>
                          </div>
                          <div>
                            {notification.type === 'tender' && (
                              <i className="bi bi-file-earmark-text text-primary"></i>
                            )}
                            {notification.type === 'stock' && (
                              <i className="bi bi-box-seam text-warning"></i>
                            )}
                            {notification.type === 'quotation' && (
                              <i className="bi bi-file-earmark-spreadsheet text-info"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleMarkAllAsRead}
                  >
                    <i className="bi bi-check-all me-2"></i>Mark All as Read
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowNotificationsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditProfileModal(false);
            }
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Profile</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditProfileModal(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                // Update user data (in a real app, this would call an API)
                setShowEditProfileModal(false);
                setToastMessage('Profile updated successfully!');
                setToastType('success');
                setShowToast(true);
                setTimeout(() => {
                  setShowToast(false);
                  setToastMessage('');
                  setToastType('success');
                }, 3000);
              }}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editProfileData.name}
                      onChange={(e) => setEditProfileData({ ...editProfileData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editProfileData.username}
                      onChange={(e) => setEditProfileData({ ...editProfileData, username: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editProfileData.email}
                      onChange={(e) => setEditProfileData({ ...editProfileData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditProfileModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowChangePasswordModal(false);
            }
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowChangePasswordModal(false)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (passwordData.newPassword !== passwordData.confirmPassword) {
                  setToastMessage('New password and confirm password do not match!');
                  setToastType('error');
                  setShowToast(true);
                  setTimeout(() => {
                    setShowToast(false);
                    setToastMessage('');
                    setToastType('success');
                  }, 3000);
                  return;
                }
                if (passwordData.newPassword.length < 6) {
                  setToastMessage('Password must be at least 6 characters long!');
                  setToastType('error');
                  setShowToast(true);
                  setTimeout(() => {
                    setShowToast(false);
                    setToastMessage('');
                    setToastType('success');
                  }, 3000);
                  return;
                }
                // In a real app, this would call an API to change password
                setShowChangePasswordModal(false);
                setPasswordData({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                });
                setToastMessage('Password successfully changed');
                setToastType('success');
                setShowToast(true);
                setTimeout(() => {
                  setShowToast(false);
                  setToastMessage('');
                  setToastType('success');
                }, 3000);
              }}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Current Password *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Password *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                    <small className="text-muted">Password must be at least 6 characters long</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Confirm New Password *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowChangePasswordModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;

